import { useParams } from "react-router-dom";
import {
  Cutlery,
  Entry,
  Exit,
  InformationCircle,
  MoonIcon,
  Pause,
  Pencil,
  Play,
  WarningCircle,
} from "../../assets/icons";
import { dateLong } from "../../utils/Date";
import { durationInDecimals, floatToTime } from "../../utils/Time";
import Badges from "../badges";
import Button from "../button";
import useSlideOver from "../slideOver/useSlideOver";
import Tooltip from "../tooltips/Tooltips";

const MissionTimeSheet = ({ mission, timeCut, isWaiting }) => {
  const { toggleSlideOver } = useSlideOver();
  const { id } = useParams();
  const isShowEditWorker = [225, 215, 210, 205].includes(
    Number(localStorage.getItem("rUser")) || 0
  );

  return (
    <>
      {isWaiting ? (
        <div className={"w-full flex flex-col gap-4 animate-pulse"}>
          <div
            className={
              "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-2"
            }
          >
            <div className={"font-bold capitalize"}>
              <div className={"h-4 bg-gray-200 rounded w-60"}></div>
            </div>
            <Button size={"SMI"} color={"SECONDARY"}>
              <div className={"h-6 bg-gray-200 rounded w-6"}></div>
            </Button>
          </div>
          <div className={"flex flex-col border rounded"}>
            {[1, 2].map((i, index) => (
              <div
                key={index}
                className={`flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between ${index !== 0 && "border-t"}`}
              >
                <div className={"flex items-center gap-4 sm:gap-6"}>
                  <div className={"h-10 bg-gray-200 rounded w-10"}></div>
                  <div className={"h-4 bg-gray-200 rounded w-14"}></div>
                </div>
                <div className={"flex items-center justify-end gap-4 sm:gap-6"}>
                  <div className={"h-4 bg-gray-200 rounded w-14"}></div>
                  <div className={"h-10 bg-gray-200 rounded w-10"}></div>
                </div>
              </div>
            ))}
          </div>
          <div className={"border rounded px-3.5 py-2.5 w-fit"}>
            <div className={"h-4 bg-gray-200 rounded w-32"}></div>
          </div>
        </div>
      ) : (
        <div
          className={"w-full flex flex-col gap-4 mission-time-sheet-container"}
        >
          <div
            className={
              "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-2"
            }
          >
            <div className={"min-w-0 font-bold capitalize"}>
              {dateLong(new Date(mission.date))}
            </div>
            <div className={"flex shrink-0 flex-wrap items-center gap-1"}>
              {mission.isFlexibilityDate && (
                <Badges color={"PRIMARY"}>
                  <div className={"flex gap-1"}>
                    Souplesse
                    <InformationCircle wh={16} color={"#374151"} />
                  </div>
                </Badges>
              )}
              {isShowEditWorker &&
                (new Date(mission.date).getTime() > new Date() ? (
                  <Tooltip
                    placement="top"
                    text="La modification n’est pas disponible pour une date future."
                  >
                    <span className="inline-flex">
                      <Button size={"SMI"} color={"DISABLED"}>
                        <Pencil wh={20} color={"#9CA3AF"} />
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip placement="top" text="Modifier">
                    <span className="inline-flex">
                      <Button
                        size={"SMI"}
                        color={"SECONDARY"}
                        name={"mission-time-sheet-container-edit"}
                        onClick={() =>
                          toggleSlideOver("edit_time_sheets", {
                            idTimeSheet: mission.idTimeSheet,
                            idMission: id,
                            dateMission: mission.date,
                          })
                        }
                      >
                        <Pencil wh={20} color={"#374151"} />
                      </Button>
                    </span>
                  </Tooltip>
                ))}
            </div>
          </div>
          <div className={"flex flex-col"}>
            {mission.markups.length > 0 ? (
              <div className={"flex flex-col rounded-lg border"}>
                {mission.markups.map((markup, index) => {
                  const filteredMarkup = Object.entries(markup).filter(
                    ([key, value]) => key === "start" || key === "end"
                  );

                  return filteredMarkup.map(([key, value], i) => {
                    if (value) {
                      const getType = () => {
                        if (key === "start") {
                          if (index === 0) {
                            return {
                              icon: (
                                <div className={"rounded-full bg-blue-500 p-2"}>
                                  <Entry wh={20} color={"#FFFFFF"} />
                                </div>
                              ),
                              name: "Entrée",
                            };
                          } else if (
                            mission.markups[index - 1]?.end
                              ? durationInDecimals(
                                  mission.markups[index - 1].end,
                                  value
                                ) <= timeCut
                              : false
                          ) {
                            return {
                              icon: (
                                <div
                                  className={"rounded-full bg-yellow-500 p-2"}
                                >
                                  <Cutlery wh={20} color={"#FFFFFF"} />
                                </div>
                              ),
                              name: "Fin de pause",
                            };
                          } else {
                            return {
                              icon: (
                                <div
                                  className={"rounded-full bg-yellow-500 p-2"}
                                >
                                  <Play wh={20} color={"#FFFFFF"} />
                                </div>
                              ),
                              name: "Fin de coupure",
                            };
                          }
                        } else if (key === "end") {
                          if (index === mission.markups.length - 1) {
                            return {
                              icon: (
                                <div className={"rounded-full bg-blue-500 p-2"}>
                                  <Exit wh={20} color={"#FFFFFF"} />
                                </div>
                              ),
                              name: "Sortie",
                            };
                          } else if (
                            mission.markups[index + 1]?.start
                              ? durationInDecimals(
                                  value,
                                  mission.markups[index + 1].start
                                ) <= timeCut
                              : false
                          ) {
                            return {
                              icon: (
                                <div
                                  className={"rounded-full bg-yellow-500 p-2"}
                                >
                                  <Cutlery wh={20} color={"#FFFFFF"} />
                                </div>
                              ),
                              name: "Début de pause",
                            };
                          } else {
                            return {
                              icon: (
                                <div
                                  className={"rounded-full bg-yellow-500 p-2"}
                                >
                                  <Pause wh={20} color={"#FFFFFF"} />
                                </div>
                              ),
                              name: "Début de coupure",
                            };
                          }
                        }
                        return { icon: "", name: "" };
                      };

                      const type = getType();

                      return (
                        <div
                          key={i}
                          className={`flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between ${(i !== 0 || index !== 0) && "border-t"}`}
                        >
                          <div className={"flex items-center gap-4 sm:gap-6"}>
                            {type && <div>{type.icon}</div>}
                            {type && <div>{type.name}</div>}
                          </div>
                          <div className={"flex justify-end sm:items-center"}>
                            {value && <div className="tabular-nums">{value}</div>}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  });
                })}
              </div>
            ) : (
              <div className={"flex gap-3 sm:gap-4"}>
                <div
                  className={
                    "flex shrink-0 justify-center self-start rounded-full bg-red-50 p-2"
                  }
                >
                  <WarningCircle wh={20} color={"#EF4444"} />
                </div>
                <div className={"min-w-0 text-sm text-gray-500"}>
                  <div className={"font-bold"}>Aucun pointage disponible</div>
                  <div>L’employé n’a pas pointé pour ce jour</div>
                </div>
              </div>
            )}
          </div>
          {mission.totalHours !== 0 && mission.markups.length > 0 && (
            <div
              className={
                "flex w-full max-w-full flex-wrap items-center gap-x-2 gap-y-2 rounded-lg border px-3.5 py-2.5 text-sm text-gray-700 sm:w-fit sm:text-base"
              }
            >
              <div>
                Total heures travaillées : {floatToTime(mission.totalHours)}
              </div>
              <div className="hidden text-gray-300 sm:block" aria-hidden>
                |
              </div>
              <div className="flex items-center gap-1">
                <div>
                  <MoonIcon wh={20} color={"#111827"} />
                </div>
                <div>
                  H. nuit :{" "}
                  {floatToTime(
                    (mission["totalHoursNight5%"] ?? 0) +
                      (mission["totalHoursNight20%"] ?? 0)
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MissionTimeSheet;
